import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/layout/PageLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
// import AdSenseAd from '../components/ads/AdSenseAd'; // Removed to prevent AdSense violations
import ImageGallery from '../components/common/ImageGallery';
import { canManageOwnedResource } from '../utils/resourcePermissions';
import { formatPrice, formatDate } from '../utils/helpers';
import { parseSocietyUrl, getSlugFromParams } from '../utils/urlHelpers';
import propertyService from '../services/propertyService';
import GoogleMapsLocationLink from '../components/common/GoogleMapsLocationLink';
import { hasGoogleMapsTarget } from '../utils/googleMaps';

const PropertyDetailPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  
  const [property, setProperty] = useState(null);
  const [relatedProperties, setRelatedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [societySlug, setSocietySlug] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState(null);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageToOwner, setMessageToOwner] = useState({ message: '', subject: '' });
  const [messageSubmitting, setMessageSubmitting] = useState(false);
  const [messageError, setMessageError] = useState(null);
  const [messageSuccess, setMessageSuccess] = useState(false);

  const propertyIdentifier = getSlugFromParams(params, 'property');
  const { isSocietySpecific, societySlug: urlSocietySlug } = parseSocietyUrl(location.pathname);

  useEffect(() => {
    if (propertyIdentifier) {
      setSocietySlug(urlSocietySlug);
      loadProperty();
      loadRelatedProperties();
      incrementViews();
    }
  }, [propertyIdentifier, urlSocietySlug]);

  useEffect(() => {
    if (propertyIdentifier) loadComments();
  }, [propertyIdentifier]);

  const loadComments = async () => {
    try {
      setCommentsLoading(true);
      const list = await propertyService.getPropertyComments(propertyIdentifier);
      setComments(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading comments:', err);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) return;
    const text = (commentText || '').trim();
    if (!text) return;
    try {
      setCommentSubmitting(true);
      setCommentError(null);
      await propertyService.createPropertyComment(propertyIdentifier, text);
      setCommentText('');
      await loadComments();
    } catch (err) {
      setCommentError(err.response?.data?.message || 'Failed to post comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleSendMessageToOwner = async (e) => {
    e.preventDefault();
    const msg = (messageToOwner.message || '').trim();
    if (!msg) {
      setMessageError('Please enter a message.');
      return;
    }
    try {
      setMessageSubmitting(true);
      setMessageError(null);
      await propertyService.contactPropertyOwner(propertyIdentifier, {
        message: msg,
        subject: (messageToOwner.subject || '').trim() || undefined,
      });
      setMessageSuccess(true);
      setMessageToOwner({ message: '', subject: '' });
      setShowMessageForm(false);
    } catch (err) {
      setMessageError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setMessageSubmitting(false);
    }
  };

  const loadProperty = async () => {
    try {
      setLoading(true);
      setError(null);
      const propertyData = await propertyService.getProperty(propertyIdentifier);
      setProperty(propertyData);
    } catch (err) {
      console.error('Error loading property:', err);
      setError('Failed to load property details');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedProperties = async () => {
    try {
      const response = await propertyService.getRelatedProperties(propertyIdentifier, {
        society: property?.society?.id,
        limit: 4
      });
      setRelatedProperties(response.results || response);
    } catch (err) {
      console.error('Error loading related properties:', err);
    }
  };

  const incrementViews = async () => {
    try {
      await propertyService.incrementViews(propertyIdentifier);
    } catch (err) {
      console.error('Error incrementing views:', err);
    }
  };

  const handleEdit = () => {
    if (societySlug) {
      navigate(`/societies/${societySlug}/properties/${propertyIdentifier}/edit`);
    } else {
      navigate(`/properties/${propertyIdentifier}/edit`);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        await propertyService.deleteProperty(propertyIdentifier);
        if (societySlug) {
          navigate(`/societies/${societySlug}/properties`);
        } else {
          navigate('/properties');
        }
      } catch (err) {
        console.error('Error deleting property:', err);
        alert('Failed to delete property');
      }
    }
  };

  const canManage = user && property && canManageOwnedResource(user, property);

  if (loading) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading property details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Property Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate(societySlug ? `/societies/${societySlug}/properties` : '/properties')}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Properties
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!property) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">🏠</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Property Not Found</h2>
            <p className="text-gray-600 mb-6">The property you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate(societySlug ? `/societies/${societySlug}/properties` : '/properties')}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
            >
              Back to Properties
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={`${property.title} - PakPoints`}>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Property Images */}
              <ImageGallery 
                images={property.images || []} 
                title="Property Images"
              />

              {/* Property Details */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h1 className="text-3xl font-bold text-gray-900">{property.title}</h1>
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                        property.property_type === 'for_rent' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {property.property_type === 'for_rent' ? 'For Rent' : 'For Sale'}
                      </span>
                    </div>
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {formatPrice(property.price)}
                    </div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{property.address}</span>
                    </div>
                    
                    {/* Property Size */}
                    {property.size && property.size_unit && (
                      <div className="flex items-center text-gray-600">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        <span className="font-medium">{property.size} {property.size_unit}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500 mb-1">Views</div>
                    <div className="text-lg font-semibold text-gray-900">{property.views || 0}</div>
                  </div>
                </div>

                {/* Property Description */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{property.description}</p>
                </div>

                {/* Contact Info */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Information</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Owner:</span> {property.user_name || 'Unknown User'}</p>
                    <p><span className="font-medium">Society:</span> {property.society?.name || 'Not specified'}</p>
                    
                    {/* Contact Phone */}
                    {property.contact_phone ? (
                      <p className="flex items-center">
                        <span className="font-medium mr-2">Phone:</span> 
                        <a 
                          href={`tel:${property.contact_phone}`}
                          className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {property.contact_phone}
                        </a>
                      </p>
                    ) : (
                      <p className="flex items-center text-gray-500">
                        <span className="font-medium mr-2">Phone:</span> 
                        <span className="text-sm">Not provided</span>
                      </p>
                    )}

                    {/* Contact Email */}
                    {property.user?.email && (
                      <p className="flex items-center">
                        <span className="font-medium mr-2">Email:</span> 
                        <a 
                          href={`mailto:${property.user.email}`}
                          className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {property.user.email}
                        </a>
                      </p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                  {canManage ? (
                    <>
                      <button
                        onClick={handleEdit}
                        className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Edit Property
                      </button>
                      <button
                        onClick={handleDelete}
                        className="bg-red-600 text-white px-6 py-3 rounded-md hover:bg-red-700 transition-colors"
                      >
                        Delete Property
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        if (property.contact_phone) {
                          window.open(`tel:${property.contact_phone}`, '_self');
                        } else if (property.user?.email) {
                          window.open(`mailto:${property.user.email}`, '_self');
                        } else {
                          alert('No contact information available');
                        }
                      }}
                      className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors"
                    >
                      Contact Owner
                    </button>
                  )}
                </div>
              </div>

              {/* Comments */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Comments</h3>
                {commentsLoading ? (
                  <p className="text-gray-500 text-sm">Loading comments...</p>
                ) : comments.length === 0 ? (
                  <p className="text-gray-500 text-sm">No comments yet.</p>
                ) : (
                  <ul className="space-y-4 mb-6">
                    {comments.map((c) => (
                      <li key={c.id} className="border-b border-gray-100 pb-3 last:border-0">
                        <p className="text-gray-900">{c.text}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {c.user_name || 'Anonymous'} · {formatDate(c.created_at)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
                {user ? (
                  <form onSubmit={handleSubmitComment} className="space-y-2">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Add a comment..."
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={commentSubmitting}
                    />
                    {commentError && <p className="text-red-600 text-sm">{commentError}</p>}
                    <button
                      type="submit"
                      disabled={commentSubmitting || !(commentText || '').trim()}
                      className="rounded-lg bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {commentSubmitting ? 'Posting...' : 'Post Comment'}
                    </button>
                  </form>
                ) : (
                  <p className="text-gray-500 text-sm">Log in to leave a comment.</p>
                )}
              </div>

              {/* Message to owner (logged-in, non-owner) */}
              {user && !canManage && (
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Message the owner</h3>
                  {messageSuccess && (
                    <p className="text-green-600 text-sm mb-4">Your message has been sent.</p>
                  )}
                  {!showMessageForm ? (
                    <button
                      type="button"
                      onClick={() => { setShowMessageForm(true); setMessageError(null); setMessageSuccess(false); }}
                      className="rounded-lg bg-green-600 text-white px-4 py-2 text-sm font-medium hover:bg-green-700"
                    >
                      Send message to owner
                    </button>
                  ) : (
                    <form onSubmit={handleSendMessageToOwner} className="space-y-3">
                      <div>
                        <label htmlFor="msg-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject (optional)</label>
                        <input
                          id="msg-subject"
                          type="text"
                          value={messageToOwner.subject}
                          onChange={(e) => setMessageToOwner((prev) => ({ ...prev, subject: e.target.value }))}
                          placeholder="e.g. Question about this property"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          disabled={messageSubmitting}
                        />
                      </div>
                      <div>
                        <label htmlFor="msg-body" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                        <textarea
                          id="msg-body"
                          value={messageToOwner.message}
                          onChange={(e) => setMessageToOwner((prev) => ({ ...prev, message: e.target.value }))}
                          placeholder="Write your message to the owner..."
                          rows={4}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          disabled={messageSubmitting}
                          required
                        />
                      </div>
                      {messageError && <p className="text-red-600 text-sm">{messageError}</p>}
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={messageSubmitting || !(messageToOwner.message || '').trim()}
                          className="rounded-lg bg-green-600 text-white px-4 py-2 text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {messageSubmitting ? 'Sending...' : 'Send message'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowMessageForm(false); setMessageError(null); setMessageToOwner({ message: '', subject: '' }); }}
                          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Related Properties */}
              {relatedProperties.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Related Properties</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {relatedProperties.map((relatedProperty) => (
                      <div
                        key={relatedProperty.id}
                        className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/properties/${relatedProperty.slug || relatedProperty.id}`)}
                      >
                        <div className="flex items-start space-x-3">
                          {relatedProperty.images && relatedProperty.images.length > 0 ? (
                            <img
                              src={relatedProperty.images[0].original_image}
                              alt={relatedProperty.title}
                              className="w-16 h-16 object-cover rounded"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 truncate">{relatedProperty.title}</h4>
                            <p className="text-sm text-blue-600 font-semibold">{formatPrice(relatedProperty.price)}</p>
                            <p className="text-xs text-gray-500 truncate">{relatedProperty.address}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                {/* Ad - Removed to avoid navigation confusion */}

                {/* Property Info */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Price:</span>
                      <span className="font-medium">{formatPrice(property.price)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Society:</span>
                      <span className="font-medium">{property.society?.name || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Posted:</span>
                      <span className="font-medium">{formatDate(property.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Views:</span>
                      <span className="font-medium">{property.views || 0}</span>
                    </div>
                    {hasGoogleMapsTarget({
                      latitude: property.latitude,
                      longitude: property.longitude,
                      address: property.address,
                    }) && (
                      <div className="pt-3 border-t">
                        <GoogleMapsLocationLink
                          latitude={property.latitude}
                          longitude={property.longitude}
                          address={property.address}
                          label="View on Map"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PropertyDetailPage;
